import React, { useEffect, useState, Fragment } from 'react';
import { toast } from 'react-toastify';
import Breadcrumb from '../common/breadcrumb';
import Loader from '../common/loader';
import api from '../../config/axios';
import utils from '../../utils/miscUtils';
import utilStyles from '../../assets/styles/utils';
import { useStateValue } from '../../store';

const { currency } = utils;
const DEFAULT_FRANCHISE_ID = '5e95f51f-5b66-4c58-b2af-7a54e8b1df1e';

const datosFacturacion = {
    accounts: [
        {
            bank: 'Banco Exterior',
            id: '0115 0114 1210 0656 2215',
            phone: 'V-20608546-7',
        },
    ],
};

const getUnitAmount = (paymentMethod, product, settings) => {
    switch (paymentMethod) {
        case 'COP':
            return Number(product.amount_cop);
        case 'USD':
            return Number(product.amount_usd);
        case 'VES':
            return Number(product.amount_usd * settings.usdves_value);
        default:
            return Number(product.amount_usd);
    }
};

const getWholesaleAmount = (paymentMethod, product, settings) => {
    switch (paymentMethod) {
        case 'COP':
            return Number(product.amount_cop_wholesale);
        case 'USD':
            return Number(product.amount_usd_wholesale);
        case 'VES':
            return Number(product.amount_usd_wholesale * settings.usdves_value);
        default:
            return Number(product.amount_usd_wholesale);
    }
};

const getAmountProduct = (quantity, paymentMethod, product, settings, optionPagoAdelantado) => {
    if (quantity >= 5 && !optionPagoAdelantado) {
        return getWholesaleAmount(paymentMethod, product, settings);
    }
    return getUnitAmount(paymentMethod, product, settings);
};

const getCopyPaymentMessage = (totalAmount, paymentMethod, optionFacturacion) => {
    if (optionFacturacion && paymentMethod.currency === 'VES') {
        return `Monto:\n*${totalAmount.slice(1)} ${paymentMethod.currency.replace('VES', 'Bs')}* \n\n Datos de pago:\n *${datosFacturacion.accounts[0].bank}*\n *${datosFacturacion.accounts[0].id}*\n *${datosFacturacion.accounts[0].phone}*`;
    }
    return utils.copyTotalAmountToClipboard(totalAmount, paymentMethod)
};

const getCopyAmountMessage = (totalAmount, paymentMethod, optionFacturacion) => {
    return `Monto: *${totalAmount.slice(1)} ${paymentMethod.currency.replace('VES', 'Bs')}*`;
};


function PriceProductItem({
    product,
    index,
    paymentMethod,
    settings,
    handleQuantity,
    optionPagoAdelantado,
    optionFacturacion,
    copyDescriptionMessage
}) {
    const isRefill = String(product.name).match(/Recarga/gi);
    let unitPrice = getUnitAmount(paymentMethod, product, settings);

    if (isRefill && product.quantity >= 5 && !optionPagoAdelantado) {
        unitPrice = getWholesaleAmount(paymentMethod, product, settings);
    }

    const lineTotal =
        product.quantity > 0
            ? product.quantity *
            getAmountProduct(product.quantity, paymentMethod, product, settings)
            : 0;

    const handleOnChangeQuantity = (action) => {
        let qty = product.quantity;
        if (action === 'sum') {
            qty += 1;
        } else {
            qty -= 1;
        }
        handleQuantity(product.id, qty);
    };

    return (
        <div
            key={`product-${index}`}
            style={{
                ...utilStyles.displayRowBetween,
                marginBottom: 16,
            }}
        >
            <div
                style={{
                    width: '50%',
                }}
            >
                <a
                    style={{
                        fontSize: 18,
                        margin: 0,
                        fontWeight: '600',
                        textDecorationLine: 'underline'
                    }}
                    href="#"
                    onClick={() => copyDescriptionMessage(product, paymentMethod, optionFacturacion)}
                >
                    {product.name}
                </a>
                <p
                    style={{
                        fontSize: 16,
                        margin: 0,
                        color: '#666',
                        fontWeight: '600',
                    }}
                >
                    {paymentMethod} {optionFacturacion ? `${currency(unitPrice)} + IVA` : `${currency(unitPrice)}`}
                </p>
            </div>
            <div
                style={{
                    width: '45%',
                }}
            >
                {product.quantity > 0 ? (
                    <div style={utilStyles.displayRowEnd}>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            onClick={() => handleOnChangeQuantity('sub')}
                        >
                            -
                        </button>
                        &nbsp;
                        <p
                            className="m-0"
                            style={{ fontSize: '1.2em', fontWeight: 'bold', color: 'black' }}
                        >
                            {product.quantity}
                        </p>
                        &nbsp;
                        <button
                            type="button"
                            className="btn btn-sm btn-outline btn-success"
                            onClick={() => handleOnChangeQuantity('sum')}
                        >
                            +
                        </button>
                    </div>
                ) : (
                    <div style={utilStyles.displayRowEnd}>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline btn-success"
                            onClick={() => handleOnChangeQuantity('sum')}
                        >
                            <i className="fa fa-plus"></i>
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

function Prices() {
    const [{ auth }] = useStateValue();
    const franchiseId = auth.user.franchiseId || DEFAULT_FRANCHISE_ID;
    const [state, setState] = useState({
        resolved: false,
        loading: true,
        products: [],
        paymentMethods: [],
        settings: null,
    });
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [optionFacturacion, setOptionFacturacion] = useState(false);
    const [optionPagoAdelantado, setOptionPagoAdelantado] = useState(false);

    const handleQuantity = (id, value) => {
        setState((prev) => ({
            ...prev,
            products: prev.products.map((product) =>
                product.id === id ? { ...product, quantity: Math.max(0, value) } : product,
            ),
        }));
    };

    useEffect(() => {
        const loadData = async () => {
            setState((prev) => ({ ...prev, loading: true, resolved: false }));
            try {
                const [productsRes, paymentMethodsRes, settingsRes] = await Promise.all([
                    api.get(`/franchise/${franchiseId}/products`),
                    api.get(`/franchise/${franchiseId}/payment_method`),
                    api.get(`/franchise/${franchiseId}/settings`),
                ]);

                const paymentMethods = paymentMethodsRes.data.data || [];
                const settingsData = settingsRes.data.data;
                const settings = Array.isArray(settingsData) ? settingsData[0] : settingsData;
                const defaultPaymentMethod =
                    paymentMethods.find((pm) => pm.currency === 'VES') ||
                    paymentMethods[0] ||
                    null;

                setState({
                    resolved: true,
                    loading: false,
                    products: (productsRes.data.data || []).map((p) => ({ ...p, quantity: 0 })),
                    paymentMethods,
                    settings,
                });
                setSelectedPaymentMethod(defaultPaymentMethod);
            } catch (error) {
                toast.error('Error cargando los precios. Intenta nuevamente.');
                setState({
                    resolved: true,
                    loading: false,
                    products: [],
                    paymentMethods: [],
                    settings: null,
                });
            }
        };

        loadData();
    }, [franchiseId]);

    const paymentCurrency = selectedPaymentMethod
        ? selectedPaymentMethod.currency
        : 'VES';

    const settings = state.settings || { usdves_value: 1 };

    const grandTotal = state.products
        .filter((p) => p.quantity > 0)
        .reduce(
            (acc, product) =>
                acc +
                product.quantity *
                getAmountProduct(product.quantity, paymentCurrency, product, settings, optionPagoAdelantado),
            0,
        );

    const copyTotalAmountToClipboard = (message) => {
        navigator.clipboard.writeText(message);
        toast.success('Respuesta copiada al portapapeles');
    };

    const totalFacturacion = optionFacturacion ? (grandTotal * 0.16 + grandTotal) : grandTotal;
    const totalToShow = Number(totalFacturacion).toFixed(2);

    const copyPaymentMessage =
        grandTotal > 0 &&
            selectedPaymentMethod &&
            selectedPaymentMethod.accounts != null
            ? getCopyPaymentMessage(currency(totalFacturacion), selectedPaymentMethod, optionFacturacion)
            : null;

    const copyOnlyAmountMessage =
        grandTotal > 0 &&
            selectedPaymentMethod &&
            selectedPaymentMethod.accounts != null
            ? getCopyAmountMessage(currency(totalFacturacion), selectedPaymentMethod, optionFacturacion)
            : null;

    const copyDescriptionMessage = (product, selectedPaymentMethod, optionFacturacion) => {
        let responseMessage = ''
        const amountProduct = getUnitAmount(selectedPaymentMethod, product, settings);
        const totalAmountProduct = optionFacturacion ? (amountProduct * 0.16 + amountProduct) : amountProduct;

        if (optionFacturacion && selectedPaymentMethod === 'VES') {
            responseMessage = `*${product.name}* (${product.description})\n\nMonto: ${currency(totalAmountProduct).slice(1)} Bs (IVA incluído)`
        } else {
            responseMessage = `*${product.name}* (${product.description})\n\nMonto: ${currency(totalAmountProduct).slice(1)} ${selectedPaymentMethod.replace('VES', 'Bs')}`
        }

        copyTotalAmountToClipboard(responseMessage)
    }


    return (
        <Fragment>
            <div className="container-fluid">
                <div className="row">
                    <div className="col-sm-12">
                        <div className="card mt-1">
                            <div className="card-body">
                                <div className="row product-adding">
                                    <div className="col-xl-8 offset-xl-2">
                                        {state.paymentMethods.length > 0 && (
                                            <div className="form form-label-center">
                                                <div className="form-group mb-0">
                                                    <label
                                                        className="col-xl-3 col-sm-4 mb-0"
                                                        style={{
                                                            fontWeight: '600',
                                                            color: '#666',
                                                        }}
                                                    >
                                                        Forma de pago
                                                    </label>
                                                    <div className="col-xl-6 col-sm-7 p-0 mt-1">
                                                        <div className="d-flex flex-column">
                                                            {state.paymentMethods.map((pm) => (
                                                                <div
                                                                    key={pm.id}
                                                                    className="d-flex align-items-center col-xl-6 col-sm-7 p-0 mb-1"
                                                                >
                                                                    <input
                                                                        type="radio"
                                                                        className="mr-2"
                                                                        id={`pricePaymentMethod-${pm.id}`}
                                                                        name="pricePaymentMethod"
                                                                        checked={
                                                                            !!selectedPaymentMethod &&
                                                                            selectedPaymentMethod.id ===
                                                                            pm.id
                                                                        }
                                                                        onChange={() =>
                                                                            setSelectedPaymentMethod(
                                                                                pm,
                                                                            )
                                                                        }
                                                                    />
                                                                    &nbsp;&nbsp;
                                                                    <label
                                                                        htmlFor={`pricePaymentMethod-${pm.id}`}
                                                                        style={{
                                                                            cursor: 'pointer',
                                                                            margin: 0,
                                                                        }}
                                                                        className={
                                                                            !!selectedPaymentMethod &&
                                                                                selectedPaymentMethod.id ===
                                                                                pm.id
                                                                                ? 'selected'
                                                                                : ''
                                                                        }
                                                                    >
                                                                        {pm.name}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="row product-adding">
                                <div className="col-xl-8 offset-xl-2">
                                    <div className="needs-validation add-product-form mt-0">
                                        <div className="form form-label-center pl-3">
                                            <hr />
                                            <div className="form-group">
                                                <div className="col-xl-9 col-sm-7 p-0">
                                                    <input
                                                        type="checkbox"
                                                        className="mr-2"
                                                        id="optionFacturacion"
                                                        name="optionFacturacion"
                                                        checked={optionFacturacion}
                                                        onChange={() =>
                                                            setOptionFacturacion(
                                                                !optionFacturacion,
                                                            )
                                                        }
                                                    />
                                                    &nbsp;&nbsp;
                                                    <label
                                                        htmlFor="optionFacturacion"
                                                        style={{
                                                            cursor: 'pointer',
                                                            margin: 0,
                                                        }}
                                                        className={
                                                            optionFacturacion ? 'selected' : ''
                                                        }
                                                    >
                                                        Facturar
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-sm-12">
                        <div className="card">
                            {state.loading && (
                                <div style={{ display: 'flex', height: 150 }}>
                                    <Loader />
                                </div>
                            )}
                            {state.resolved && !state.loading && (
                                <div className="card-body">
                                    <div className="row product-adding">
                                        <div className="col-xl-8 offset-xl-2">
                                            <div className="needs-validation add-product-form mt-0">
                                                <div className="form form-label-center">
                                                    <div className="form-group">
                                                        <div className="col-xl-9 col-sm-7 p-0">
                                                            {state.products.length === 0 ? (
                                                                <p className="text-muted">
                                                                    No hay productos disponibles.
                                                                </p>
                                                            ) : (
                                                                state.products.map(
                                                                    (product, index) => (
                                                                        <PriceProductItem
                                                                            key={`product-${product.id}-${index}`}
                                                                            product={product}
                                                                            index={index}
                                                                            paymentMethod={
                                                                                paymentCurrency
                                                                            }
                                                                            settings={settings}
                                                                            handleQuantity={
                                                                                handleQuantity
                                                                            }
                                                                            optionPagoAdelantado={optionPagoAdelantado}
                                                                            optionFacturacion={optionFacturacion}
                                                                            copyDescriptionMessage={copyDescriptionMessage}
                                                                        />
                                                                    ),
                                                                )
                                                            )}
                                                        </div>
                                                    </div>
                                                    {grandTotal > 0 && selectedPaymentMethod && (
                                                        <div className="form-group mb-3 mt-1 row text-center">
                                                            <div className="col-xl-12 col-sm-12 p-0">
                                                                <p
                                                                    className="m-0"
                                                                    style={{
                                                                        fontSize: '2em',
                                                                        color: 'black',
                                                                    }}
                                                                >
                                                                    <strong>
                                                                        {paymentCurrency}{' '}
                                                                        {currency(totalFacturacion)}
                                                                    </strong>
                                                                </p>
                                                                <br />
                                                                {copyPaymentMessage && (
                                                                    <Fragment>
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-primary"
                                                                            onClick={() =>
                                                                                copyTotalAmountToClipboard(
                                                                                    copyOnlyAmountMessage,
                                                                                )
                                                                            }
                                                                        >
                                                                            <i className="fa fa-copy"></i> Monto
                                                                        </button>
                                                                        <br />
                                                                        <br />
                                                                        <button
                                                                            type="button"
                                                                            className="btn btn-sm btn-outline-success"
                                                                            onClick={() =>
                                                                                copyTotalAmountToClipboard(
                                                                                    copyPaymentMessage,
                                                                                )
                                                                            }
                                                                        >
                                                                            <i className="fa fa-copy"></i> Datos de pago
                                                                        </button>
                                                                    </Fragment>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
}

export default Prices;
